import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "../../styles/Home.css";
import Header from "../../components/Header";
import { GetSleepByCategoryId } from "../../services/Api";
import { FaPlay, FaPause, FaBackward, FaForward } from "react-icons/fa";

// Utility to convert seconds to mm:ss format
const formatDuration = (durationInSeconds) => {
  const totalSeconds = parseInt(durationInSeconds, 10);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const SleepDetails = () => {
  const location = useLocation();
  const sleepCategoryId = location.state?.sleepCategoryId || "1";
  const sleepCategoryTitle =
    location.state?.sleepCategoryTitle || "Sleep Wellness";
  const sleepCategoryImageUrl = location.state?.sleepCategoryImageUrl || "";

  const [audios, setAudios] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);

  useEffect(() => {
    const fetchAudios = async () => {
      const res = await GetSleepByCategoryId(sleepCategoryId);
      if (res && res.data) {
        setAudios(res.data);
        setCurrentAudio(res.data[0]); // autoplay first item
      }
    };
    fetchAudios();
  }, [sleepCategoryId]);

  const handleAudioPlay = (audio) => {
    if (audioRef) {
      audioRef.pause();
    }
    setCurrentAudio(audio);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (currentAudio) {
      const audio = new Audio(currentAudio.audioFilePresignedUrl);
      audio.play();
      setAudioRef(audio);

      audio.onended = () => {
        setIsPlaying(false);
      };

      return () => audio.pause();
    }
  }, [currentAudio]);

  const togglePlayback = () => {
    if (!audioRef) return;

    if (isPlaying) {
      audioRef.pause();
      setIsPlaying(false);
    } else {
      audioRef.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="page-container relative bg-white min-h-screen">
      <Header />

      {/* Top Audio Player */}
      {currentAudio && (
        <div className="w-full max-w-3xl mx-auto bg-cyan-200 rounded-lg shadow-md mt-6 px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {sleepCategoryImageUrl ? (
              <img
                src={sleepCategoryImageUrl}
                alt="Sleep Category"
                className="w-24 h-24 rounded-full object-cover bg-white"
              />
            ) : (
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-3xl">
                🎧
              </div>
            )}

            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-800">
                {sleepCategoryTitle}
              </h2>
              <h3 className="text-lg text-gray-700 mt-1">
                {currentAudio.audioTitle}
              </h3>
              <p className="text-gray-600">{currentAudio.audioDescription}</p>
            </div>
          </div>

          <div className="flex justify-center items-center mt-4 gap-6 text-2xl text-gray-700">
            <FaBackward className="cursor-pointer" />
            {isPlaying ? (
              <FaPause className="cursor-pointer" onClick={togglePlayback} />
            ) : (
              <FaPlay className="cursor-pointer" onClick={togglePlayback} />
            )}
            <FaForward className="cursor-pointer" />
          </div>
        </div>
      )}

      {/* Audio Cards */}
      <div className="w-full flex flex-col items-center gap-6 mt-10 pb-10">
        {audios.map((audio) => (
          <div
            key={audio.podcastId}
            className="w-full max-w-3xl bg-gray-50 shadow-md rounded-lg p-4 cursor-pointer hover:shadow-lg transition"
            onClick={() => handleAudioPlay(audio)}
          >
            <div className="flex items-center gap-4">
              <button className="bg-cyan-500 text-white rounded-full p-3">
                <FaPlay />
              </button>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 text-base">
                  {audio.audioTitle}
                </h3>
                <p className="text-gray-600 text-sm">
                  {audio.audioDescription}
                </p>
              </div>
              <span className="text-sm text-gray-400">
                {audio.audioDuration && audio.audioDuration !== "0"
                  ? formatDuration(audio.audioDuration)
                  : "0:00"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SleepDetails;
